// src/services/avatarService.js

import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase/firebase';

/**
 * Sets up the default CrewChain avatar for a user if they don't have one
 * @param {string} userId - The user's UID
 * @param {Object} userData - The user's current data from Firestore
 * @returns {Promise<boolean>} - Returns true if avatar was set, false if user already had one
 */
export const setupDefaultAvatar = async (userId, userData) => {
  try {
    // Check if user already has a profile picture
    if (userData?.photoURL || userData?.profilePictureUrl) {
      console.log('User already has a profile picture, skipping default avatar setup');
      return false;
    }

    // Set the default CrewChain avatar
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      photoURL: '/crewchainDefault.png',
      profilePictureUrl: '/crewchainDefault.png',
      defaultAvatarSet: true,
      updatedAt: new Date()
    });

    console.log('✅ Default CrewChain avatar set for user:', userId);
    return true;
  } catch (error) {
    console.error('Error setting up default avatar:', error);
    throw error;
  }
};

/**
 * Checks if a user needs a default avatar setup
 * @param {Object} userData - The user's data from Firestore
 * @returns {boolean} - Returns true if user needs default avatar
 */
export const needsDefaultAvatar = (userData) => {
  return !userData?.photoURL && !userData?.profilePictureUrl && !userData?.defaultAvatarSet;
};

/**
 * Gets the appropriate avatar URL for a user
 * @param {Object} userData - The user's data from Firestore
 * @returns {string} - The avatar URL to use
 */
export const getUserAvatarUrl = (userData) => {
  // Prioritize Firebase Storage URL (profilePictureUrl) over legacy photoURL
  // This ensures uploaded profile pictures are shown instead of the default
  if (userData?.profilePictureUrl && userData.profilePictureUrl !== '/crewchainDefault.png') {
    return userData.profilePictureUrl;
  }
  
  if (userData?.photoURL && userData.photoURL !== '/crewchainDefault.png') {
    return userData.photoURL;
  }
  
  return '/crewchainDefault.png';
};
